import React from 'react';
import { Clock, Package, Percent, Gift, BookOpen, Check, ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { SPECIFIC_BUNDLE_PRICING, SPECIFIC_DISCOUNTS, openHotmart } from './coursesConstants';

export const SpecificBundleCard = ({ bundle, fmt, currency, bundleCheckout, navigate, onEnrollmentRequest, onConsult }) => {
  const pricing = SPECIFIC_BUNDLE_PRICING[bundle.id];
  const checkout = bundleCheckout[bundle.id];
  return (
    <Card className="bg-zinc-900 border-zinc-800 overflow-hidden flex-shrink-0 w-[350px] md:w-[400px] snap-start hover:border-red-500/50 transition-all duration-300">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <CardTitle className="text-xl text-white mb-1">{bundle.title}</CardTitle>
            <CardDescription className="text-gray-300 text-sm line-clamp-2">
              {bundle.description}
            </CardDescription>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-black/30 rounded-lg p-2 text-center">
            <Clock className="text-red-500 mx-auto mb-1" size={18} />
            <div className="text-lg font-bold text-white">{bundle.total_hours}h</div>
            <div className="text-xs text-gray-400">Carga Horária</div>
          </div>
          <div className="bg-black/30 rounded-lg p-2 text-center">
            <Package className="text-red-500 mx-auto mb-1" size={18} />
            <div className="text-lg font-bold text-white">{bundle.course_ids.length}</div>
            <div className="text-xs text-gray-400">Cursos</div>
          </div>
          <div className="bg-black/30 rounded-lg p-2 text-center">
            <Percent className="text-red-500 mx-auto mb-1" size={18} />
            <div className="text-lg font-bold text-green-400">{bundle.savings_percentage}%</div>
            <div className="text-xs text-gray-400">Economia</div>
          </div>
          <div className="bg-black/30 rounded-lg p-2 text-center">
            <Gift className="text-green-500 mx-auto mb-1" size={18} />
            <div className="text-xs font-bold text-green-400">+Bônus</div>
            <div className="text-xs text-gray-400">Design Review</div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="bg-black/20 rounded-lg p-3">
          <h4 className="text-white font-semibold mb-2 flex items-center text-sm">
            <BookOpen size={14} className="mr-2 text-red-500" />
            Cursos Incluídos
          </h4>
          <div className="space-y-1">
            {(bundle.syllabus || []).slice(0, 4).map((item, index) => (
              <div key={index} className="flex items-center space-x-2 text-xs text-gray-300">
                <Check size={10} className="text-green-400 flex-shrink-0" />
                <span className="line-clamp-1">{item}</span>
              </div>
            ))}
            {(bundle.syllabus?.length > 4 || bundle.course_ids.length > 4) && (
              <div className="text-xs text-gray-400 italic">
                +{Math.max((bundle.syllabus?.length || bundle.course_ids.length) - 4, 0)} cursos...
              </div>
            )}
          </div>
        </div>

        {pricing ? (
          <div className="bg-zinc-800/50 rounded-lg p-3" data-testid={`bundle-pricing-${bundle.id}`}>
            <div className="text-xs text-gray-400 mb-1 text-center">
              Investimento{pricing.eadOnly ? ' · Exclusivamente EaD Gravado' : ''}
            </div>
            <div className="text-center">
              <span className="text-2xl font-bold text-white">{fmt(bundle.prices.ead_recorded)}</span>
            </div>
            {currency === 'BRL' && (
              <div className="text-xs text-gray-400 text-center">
                ou {pricing.count}x de {fmt(bundle.prices.ead_recorded / pricing.count)}
              </div>
            )}
            <div className="text-[11px] text-gray-500 text-center mb-2">
              Pagamento em {pricing.methods}
            </div>
            <div className="border-t border-zinc-700 pt-2 space-y-1">
              <div className="text-[11px] text-gray-400 text-center mb-1">
                Descontos (mediante comprovação de vínculo):
              </div>
              {SPECIFIC_DISCOUNTS.map((d) => (
                <div key={d.label} className="flex items-center justify-between text-xs gap-2">
                  <span className={d.color}>{d.label} <span className="opacity-70">({d.off}% OFF)</span></span>
                  <span className="text-white font-semibold whitespace-nowrap">{fmt(bundle.prices.ead_recorded * d.factor)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-400 mb-1">Investimento</div>
            <div className="text-lg font-bold text-white">Valores sob consulta</div>
            <div className="text-xs text-gray-400">Condições personalizadas com nossos consultores</div>
          </div>
        )}

        <div className="space-y-2">
          <Button
            onClick={() => navigate(`/formation/${bundle.id}`)}
            variant="outline"
            size="sm"
            className="w-full border-red-600 text-red-500 hover:bg-red-600 hover:text-white text-sm"
          >
            <BookOpen className="mr-1" size={14} />
            Ver Programa Completo
          </Button>

          {pricing ? (
            checkout?.soon ? (
              <>
                <div className="flex items-center justify-center">
                  <Badge className="bg-zinc-700 text-gray-200 px-4 py-1.5 text-sm" data-testid={`bundle-soon-${bundle.id}`}>
                    EM BREVE
                  </Badge>
                </div>
                <a
                  href={`https://wa.me/5561995792667?text=${encodeURIComponent(`Olá! Tenho interesse na "${bundle.title}". Poderiam me avisar quando estiver disponível?`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full bg-green-600 hover:bg-green-700 text-white rounded-md py-2 text-sm font-semibold transition-colors"
                  data-testid={`bundle-whatsapp-${bundle.id}`}
                >
                  <MessageSquare className="mr-2" size={16} />
                  Avise-me pelo WhatsApp
                </a>
              </>
            ) : (
              <>
                <Button
                  onClick={() => openHotmart(checkout?.padrao)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white group"
                  data-testid={`enroll-bundle-${bundle.id}`}
                >
                  Matricular (EaD Gravado)
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </Button>
                <button
                  type="button"
                  onClick={() => onEnrollmentRequest({ bundle, label: 'Desconto (comprovação de vínculo)' })}
                  className="w-full text-xs text-teal-300 hover:text-teal-200 underline underline-offset-2"
                  data-testid={`enroll-discount-bundle-${bundle.id}`}
                >
                  Solicitar matrícula com desconto (enviar documentos)
                </button>
              </>
            )
          ) : (
            <Button
              onClick={() => onConsult(bundle)}
              className="w-full bg-red-600 hover:bg-red-700 text-white group"
              data-testid={`consult-bundle-${bundle.id}`}
            >
              <MessageSquare className="mr-2" size={16} />
              Agende uma conversa
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SpecificBundleCard;
